/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CrossIcon, Inscription, Text, useResponsive } from '@common-fe/common-fe';
import styled from 'styled-components';

import { FileType } from '@/common/constants';
import useFilesInDocumentStore from '@/components/controls/DropScan/useFilesInDocument.store';

import PdfThumb from './PdfScanViewer/PdfThumb';
import TiffThumb from './TiffScanViewer/TiffThumb';
import { CloseButton, ImgSquare, ImgThumbInner, ImgThumbOuter, Wrapper } from './ScanPreview.styles';

const PreviewTooltipWrapper = styled(Box)`
  position: absolute;
  right: -205px;
  top: -6px;
  z-index: 1;
  width: 210px;
  max-width: initial;
  &:before {
    content: '';
    position: absolute;
    width: 10px;
    height: 10px;
    top: calc(100% - 48px);
    left: -5px;
    background-color: ${({ theme }) => theme.colors.background3};
    transform: rotate(-45deg);
    border-radius: 3px 0 0 0;
  }
`;
export const PreviewTooltip: React.FC<{ setShowTooltip: (val: boolean) => void }> = ({ setShowTooltip }) => (
  <PreviewTooltipWrapper
    round="container1Round"
    direction="row"
    background="background3"
    align="center"
    style={{ zIndex: 2000 }}
    pad={{ vertical: 'spacing12', horizontal: 'spacing16' }}
  >
    <Box margin={{ right: 'spacing16' }}>
      <Text weight={500} size="small" color="textOnColor">
        Claim details will be scanned automatically from this file.
      </Text>
    </Box>
    <Box margin={{ right: 'spacing6' }} onClick={() => setShowTooltip(false)}>
      <CrossIcon size="10px" color="iconOnColor" />
    </Box>
  </PreviewTooltipWrapper>
);

export const ScanPreview = ({
  storeKey,
  isPreviewHidden,
  hasMainLabel,
  isSmallViewVersion,
  isFlex,
  selectedIndex,
  onSelect,
  onDelete,
  isAddDocFirst,
  children,
  isNumberOfImageHidden,
  isThumbsTopPosition,
  overrideFiles,
}: any) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const {
    values: { files: storeFiles },
  } = useFilesInDocumentStore(storeKey);
  const [passwordProtectedIds, setPasswordProtectedIds] = React.useState<Record<string, boolean>>({});

  const files = overrideFiles ?? storeFiles;
  const thumbs = files.map((file: any, index: number) => {
    if (passwordProtectedIds[file.temporalId]) {
      return null;
    }

    let thumbContent: React.ReactNode;

    switch (file.type) {
      case FileType.PDF:
        thumbContent = (
          <PdfThumb
            url={file.url}
            alt={file.name}
            onPasswordProtectedChange={(isPasswordProtected) => {
              setPasswordProtectedIds((prevState) => {
                if (!!prevState[file.temporalId] === isPasswordProtected) {
                  return prevState;
                }
                return { ...prevState, [file.temporalId]: isPasswordProtected };
              });
            }}
          />
        );
        break;
      case FileType.TIF:
        thumbContent = <TiffThumb url={file.url} alt={file.name} />;
        break;
      case FileType.TIFF:
        thumbContent = <TiffThumb url={file.url} alt={file.name} />;
        break;
      default:
        thumbContent = <ImgSquare src={file.preview || file.url} alt={file.name} />;
    }

    return (
      <Box key={file.temporalId}>
        <ImgThumbOuter
          active={index === selectedIndex}
          key={file.preview || file.url}
          onClick={() => onSelect(index)}
        >
          <ImgThumbInner>
            {onDelete ? (
              <CloseButton
                data-testid="close_button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(index);
                }}
              >
                <CrossIcon color="iconOnColor" size="12px" />
              </CloseButton>
            ) : null}

            {isNumberOfImageHidden ? null : (
              <Box
                data-testid="number_of_image"
                width="20px"
                height="20px"
                background="background3"
                justify="center"
                align="center"
                round="button2Round"
                style={{ position: 'absolute', top: '2px', left: '2px', zIndex: 2 }}
              >
                <Inscription size="12px" color="textOnColor">
                  {index + 1}
                </Inscription>
              </Box>
            )}

            {index === 0 && isSmallViewVersion && (
              <Box
                background="button"
                justify="center"
                align="center"
                width="100%"
                round={{ corner: 'bottom', size: '5px' }}
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                }}
              >
                {hasMainLabel && (
                  <Inscription size="small" weight={500} color="textOnColor">
                    {t('Main')}
                  </Inscription>
                )}
              </Box>
            )}
            {thumbContent}
          </ImgThumbInner>
        </ImgThumbOuter>
      </Box>
    );
  });

  return (
    <Wrapper
      isSmallViewVersion={isSmallViewVersion}
      isFlex={isFlex}
      direction={isSmallViewVersion || isMobile || isThumbsTopPosition ? 'row' : 'column'}
      gap="spacing8"
    >
      {isAddDocFirst && children}
      {files.length && !isPreviewHidden ? thumbs : null}
      {!isAddDocFirst && children}
    </Wrapper>
  );
};

export default ScanPreview;
